import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const TOAST_DURATION = 4000;

const api = {
  list: (search = "", isActive = "") => axiosInstance.get("/api/superadmin/master/validities/", { params: { q: search, is_active: isActive } }),
  get: (id) => axiosInstance.get(`/api/superadmin/master/validities/${id}/`),
  create: (body) => axiosInstance.post("/api/superadmin/master/validities/", body),
  update: (id, body) => axiosInstance.patch(`/api/superadmin/master/validities/${id}/`, body),
  remove: (id) => axiosInstance.delete(`/api/superadmin/master/validities/${id}/`),
};
  
//   update: (id, body) =>
//     fetch(`${BASE_URL}/api/superadmin/master/validities/${id}/`, {
//       method: "PATCH",
//       headers: authHeaders(),
//       body: JSON.stringify(body),
//     }),
//   remove: (id) =>
//     fetch(`${BASE_URL}/api/superadmin/master/validities/${id}/`, {
//       method: "DELETE",
//       headers: authHeaders(),
//     }),
// };

// ── Icons ──────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const WarningIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#FEF3C7" />
    <path d="M24 13L11 37h26L24 13z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" fill="#FDE68A" />
    <path d="M24 22v7M24 31.5v1" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#DCFCE7" />
    <circle cx="24" cy="24" r="14" stroke="#16a34a" strokeWidth="2" fill="#BBF7D0" />
    <path d="M17 24l5 5 9-9" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Toast ──────────────────────────────────────────────────────────────────────
let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error: (title, msg) => toast._push({ type: "error", title, msg }),
  info: (title, msg) => toast._push({ type: "info", title, msg }),
  loading: (title) => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id) => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error: { bar: "#dc2626", icon: "✕" },
  info: { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{
      position: "relative", overflow: "hidden", background: "#fff",
      borderRadius: 10, borderLeft: `4px solid ${meta.bar}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
      minWidth: 300, maxWidth: 360,
      animation: exiting ? "vmOut 0.28s ease forwards" : "vmIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: "2px", flexShrink: 0, display: "flex", borderRadius: 4, transition: "color 0.15s", marginTop: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

// ── Shared UI Primitives ───────────────────────────────────────────────────────
const Toggle = ({ value, onChange }) => (
  <div onClick={() => onChange(!value)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
    <div style={{ width: 42, height: 24, borderRadius: 12, background: value ? "#22c55e" : "#cbd5e1", position: "relative", transition: "background 0.2s" }}>
      <div style={{ position: "absolute", top: 4, left: value ? 22 : 4, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: value ? "#16a34a" : "#94a3b8" }}>{value ? "Active" : "Inactive"}</span>
  </div>
);

const Badge = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, ...(active ? { background: "#dcfce7", color: "#16a34a" } : { background: "#fee2e2", color: "#dc2626" }) }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#22c55e" : "#f43f5e", display: "inline-block" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const Modal = ({ title, onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", animation: "vmFadeUp 0.2s ease" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 4, borderRadius: 6 }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#64748b"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}>
            <CloseIcon />
          </button>
        </div>
        <div style={{ padding: "20px 24px 24px", maxHeight: "80vh", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
};

const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    {[40, 130, 80, 80, 100, 90, 130, 80].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <div style={{ height: 13, width: w, maxWidth: "100%", borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "vmShimmer 1.4s infinite" }} />
      </td>
    ))}
  </tr>
);
const TableSkeleton = () => <>{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</>;

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);
  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };
  const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 34, height: 34, padding: "0 6px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: "#fff", color: "#374151", transition: "all 0.15s", lineHeight: 1 };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>Showing <strong style={{ color: "#374151" }}>{startItem}–{endItem}</strong> of <strong style={{ color: "#374151" }}>{totalCount}</strong> results</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}><ChevronLeftIcon /></button>
        {getPages().map((p, i) =>
          p === "..." ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 13, lineHeight: "34px" }}>…</span>
            : <button key={p} onClick={() => onPageChange(p)} className={p === currentPage ? "" : "vm-page-btn"}
              style={{ ...btnBase, ...(p === currentPage ? { background: "#3b82f6", color: "#fff", border: "1px solid #3b82f6", fontWeight: 600 } : {}) }}>{p}</button>
        )}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}><ChevronRightIcon /></button>
      </div>
    </div>
  );
};

// ── Stats Bar ──────────────────────────────────────────────────────────────────


// ── Delivery Timeline Visual ───────────────────────────────────────────────────
const TIMELINE_COLORS = [
  { bg: "#dbeafe", bar: "#3b82f6", text: "#1d4ed8", dot: "#2563eb" },
  { bg: "#d1fae5", bar: "#10b981", text: "#065f46", dot: "#059669" },
  { bg: "#fef3c7", bar: "#f59e0b", text: "#92400e", dot: "#d97706" },
  { bg: "#ede9fe", bar: "#8b5cf6", text: "#4c1d95", dot: "#7c3aed" },
  { bg: "#fce7f3", bar: "#ec4899", text: "#831843", dot: "#db2777" },
  { bg: "#cffafe", bar: "#06b6d4", text: "#164e63", dot: "#0891b2" },
  { bg: "#fed7aa", bar: "#f97316", text: "#7c2d12", dot: "#ea580c" },
];



// ── Confirm Dialogs ────────────────────────────────────────────────────────────
const DeactivateConfirm = ({ validity, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><WarningIcon /></div>
    <p style={{ fontSize: 16, fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Deactivate <span style={{ color: "#f43f5e" }}>"{validity.label}"</span>?
    </p>
    <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 24px" }}>This will mark the validity period as inactive. You can reactivate it later.</p>
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
      <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#f43f5e", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Deactivating…" : "Yes, Deactivate"}
      </button>
    </div>
  </div>
);

const ActivateConfirm = ({ validity, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><CheckCircleIcon /></div>
    <p style={{ fontSize: 16, fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Activate <span style={{ color: "#16a34a" }}>"{validity.label}"</span>?
    </p>
    <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 24px" }}>This will mark the validity as active and make it available for use.</p>
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
      <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#16a34a", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Activating…" : "Yes, Activate"}
      </button>
    </div>
  </div>
);

// ── Validity Form ──────────────────────────────────────────────────────────────
const UNIT_OPTIONS = [
  { value: "day", label: "Day(s)" },
  { value: "week", label: "Week(s)" },
  { value: "month", label: "Month(s)" },
];

const EMPTY_FORM = { label: "", value: "", unit: "month", sort_order: "", is_active: true };

/**
 * existingValidities: all validities from list (for duplicate label check)
 * editingId: id of the record being edited (excluded from duplicate check), null for create
 */
const DeliveryTimelineForm = ({ initial, onSubmit, onCancel, loading, existingValidities = [], editingId = null }) => {
  const isEdit = !!initial;

  const [form, setForm] = useState(
    isEdit
      ? {
          label: initial.label ?? "",
          value: initial.value ?? "",
          unit: initial.unit ?? "month",
          sort_order: initial.sort_order ?? "",
          is_active: initial.is_active ?? true,
        }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  // Auto-generate label when value or unit changes (only for new records if label is empty or was auto-generated)
  const autoLabel = useRef(true);
  const handleValueUnitChange = (key, val) => {
    set(key, val);
    if (!isEdit && autoLabel.current) {
      const newValue = key === "value" ? val : form.value;
      const newUnit = key === "unit" ? val : form.unit;
      if (newValue) {
        const unitLabel = UNIT_OPTIONS.find((u) => u.value === newUnit)?.label || newUnit;
        const generated = `${newValue} ${unitLabel.replace("(s)", Number(newValue) === 1 ? "" : "s").trim()}`;
        setForm((f) => ({ ...f, [key]: val, label: generated }));
        setErrors((e) => ({ ...e, label: "", [key]: "" }));
      }
    }
  };

  // Duplicate label check (case-insensitive, trimmed)
  const isDuplicateLabel = (label) => {
    const normalized = label.trim().toLowerCase();
    return existingValidities.some(
      (v) => v.label.trim().toLowerCase() === normalized && v.id !== editingId
    );
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) {
      e.label = "Label is required.";
    } else if (isDuplicateLabel(form.label)) {
      e.label = `A validity with the label "${form.label.trim()}" already exists. Please use a unique label.`;
    }
    if (form.value === "" || form.value === null || form.value === undefined) {
      e.value = "Value is required.";
    } else if (isNaN(Number(form.value)) || Number(form.value) <= 0) {
      e.value = "Value must be a positive number.";
    }
    if (!form.unit) e.unit = "Unit is required.";
    if (form.sort_order !== "" && (isNaN(Number(form.sort_order)) || Number(form.sort_order) < 0)) {
      e.sort_order = "Sort order must be a non-negative number.";
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSubmit(
      {
        label: form.label.trim(),
        value: Number(form.value),
        unit: form.unit,
        sort_order: form.sort_order !== "" ? Number(form.sort_order) : undefined,
        is_active: form.is_active,
      },
      setErrors
    );
  };

  const inputStyle = (key) => ({
    width: "100%", padding: "9px 12px",
    border: `1.5px solid ${errors[key] ? "#f43f5e" : "#e2e8f0"}`,
    borderRadius: 8, fontSize: 13.5, color: "#1e293b", outline: "none",
    background: errors[key] ? "#fff1f2" : "#f8fafc",
    boxSizing: "border-box", fontFamily: "inherit", transition: "border 0.15s",
  });
  const labelStyle = { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 };

  return (
    <div>
      {errors.__global && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>⚠ {errors.__global}</p>
        </div>
      )}

      {/* Label */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, color: errors.label ? "#f43f5e" : "#374151" }}>
          Label <span style={{ color: "#f43f5e" }}>*</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>Must be unique</span>
        </label>
        <input
          type="text"
          style={inputStyle("label")}
          placeholder="e.g. 3 Months"
          value={form.label}
          onChange={(e) => { autoLabel.current = false; set("label", e.target.value); }}
          onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
          onBlur={(e) => { e.target.style.borderColor = errors.label ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.label ? "#fff1f2" : "#f8fafc"; }}
        />
        {errors.label && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 5, marginTop: 5, padding: "6px 10px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 6 }}>
            <span style={{ fontSize: 13, color: "#f43f5e", flexShrink: 0 }}>⚠</span>
            <span style={{ fontSize: 12, color: "#dc2626" }}>{errors.label}</span>
          </div>
        )}
      </div>

      {/* Value + Unit */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ ...labelStyle, color: errors.value ? "#f43f5e" : "#374151" }}>
            Value <span style={{ color: "#f43f5e" }}>*</span>
          </label>
          <input
            type="number"
            min="1"
            step="1"
            style={inputStyle("value")}
            placeholder="e.g. 3"
            value={form.value}
            onChange={(e) => handleValueUnitChange("value", e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
            onBlur={(e) => { e.target.style.borderColor = errors.value ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.value ? "#fff1f2" : "#f8fafc"; }}
          />
          {errors.value && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors.value}</span>}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ ...labelStyle, color: errors.unit ? "#f43f5e" : "#374151" }}>
            Unit <span style={{ color: "#f43f5e" }}>*</span>
          </label>
          <select
            style={{ ...inputStyle("unit"), cursor: "pointer" }}
            value={form.unit}
            onChange={(e) => handleValueUnitChange("unit", e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
            onBlur={(e) => { e.target.style.borderColor = errors.unit ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.unit ? "#fff1f2" : "#f8fafc"; }}
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
          {errors.unit && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors.unit}</span>}
        </div>
      </div>

      {/* Sort Order */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, color: errors.sort_order ? "#f43f5e" : "#374151" }}>
          Sort Order
          <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>optional</span>
        </label>
        <input
          type="number"
          min="0"
          step="10"
          style={inputStyle("sort_order")}
          placeholder="e.g. 30"
          value={form.sort_order}
          onChange={(e) => set("sort_order", e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
          onBlur={(e) => { e.target.style.borderColor = errors.sort_order ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.sort_order ? "#fff1f2" : "#f8fafc"; }}
        />
        {errors.sort_order && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors.sort_order}</span>}
      </div>

      {/* Status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22, padding: "12px 14px", background: "#f8fafc", borderRadius: 8, border: "1.5px solid #e2e8f0" }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Status</label>
        <Toggle value={form.is_active} onChange={(v) => set("is_active", v)} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
        <button onClick={onCancel} disabled={loading}
          style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loading}
          style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#3b82f6", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, display: "flex", alignItems: "center", gap: 6 }}>
          {loading
            ? "Saving…"
            : isEdit
              ? "Update Timeline"
              : <><PlusIcon /> Add Timeline</>}
        </button>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const DeliveryTimelineMaster = () => {
  const [allValidities, setAllValidities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editValidity, setEditValidity] = useState(null);
  const [deleteValidity, setDeleteValidity] = useState(null);
  const [activateValidity, setActivateValidity] = useState(null);

  const fetchValidities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list(search, filterActive);
      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); setLoading(false); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view validities."); setLoading(false); return; }
      if (data?.status >= 400) throw new Error("Load failed");
      // Sort: active first, then by sort_order
      const sorted = [...data?.results].sort((a, b) => {
        if (b.is_active !== a.is_active) return b.is_active ? 1 : -1;
        return (a.sort_order ?? 9999) - (b.sort_order ?? 9999);
      });
      setAllValidities(sorted);
    } catch (err) {
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Failed to Load",
        err?.name === "TypeError" ? "Unable to reach the server." : "Unable to fetch validities. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [search, filterActive]);

  useEffect(() => { setCurrentPage(1); }, [search, filterActive]);
  useEffect(() => { const t = setTimeout(fetchValidities, 400); return () => clearTimeout(t); }, [fetchValidities]);

  const totalCount = allValidities.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedValidities = useMemo(
    () => allValidities.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allValidities, currentPage]
  );

  const applyFieldErrors = (data, setErrors) => {
    if (!data || typeof data !== "object" || data?.detail) return false;
    const mapped = {};
    let hasFields = false;
    Object.keys(data).forEach((k) => {
      const v = data[k];
      if (k === "non_field_errors") { mapped.__global = Array.isArray(v) ? v[0] : String(v); }
      else { mapped[k] = Array.isArray(v) ? v[0] : String(v); }
      hasFields = true;
    });
    if (hasFields) { setErrors(mapped); return true; }
    return false;
  };

  const handleAdd = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Adding Timeline…");
    try {
      const { data } = await api.create(body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) {
          toast.error("Validation Error", "Please fix the highlighted fields.");
          return;
        }
        toast.error("Create Failed", data?.data?.detail || `Failed to add Timeline (${data?.status}).`);
        return;
      }

      const created = data?.data ?? data;
      toast.success("Timeline Added!", `"${created?.label || body.label}" has been added successfully.`);
      setShowAdd(false);
      fetchValidities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", err?.name === "TypeError" ? "Unable to reach the server." : "An unexpected error occurred.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Updating Timeline…");
    try {
      const { data } = await api.update(editValidity.id, body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) {
          toast.error("Validation Error", "Please fix the highlighted fields.");
          return;
        }
        toast.error("Update Failed", data?.data?.detail || `Update failed (${data?.status}).`);
        return;
      }

      const updated = data?.data ?? data;
      toast.success("Timeline Updated!", `"${updated?.label || editValidity.label}" has been updated successfully.`);
      setEditValidity(null);
      fetchValidities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", err?.name === "TypeError" ? "Unable to reach the server." : "An unexpected error occurred.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteValidity) return;
    setActionLoading(true);
    setDeletingId(deleteValidity.id);
    const tid = toast.loading(`Deactivating "${deleteValidity.label}"…`);
    try {
      const { data } = await api.remove(deleteValidity.id);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        toast.error("Deactivate Failed", data?.data?.detail || `Deactivation failed (${data?.status}).`);
        return;
      }

      toast.success("Deactivated!", data?.data?.message || `"${deleteValidity.label}" has been deactivated.`);
      setDeleteValidity(null);
      fetchValidities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete deactivation. Please try again.");
    } finally {
      setActionLoading(false);
      setDeletingId(null);
    }
  };

  const handleActivateConfirm = async () => {
    if (!activateValidity) return;
    setActivatingId(activateValidity.id);
    const tid = toast.loading(`Activating "${activateValidity.label}"…`);
    try {
      const { data } = await api.update(activateValidity.id, { is_active: true });
      toast.dismiss(tid);

      if (data?.status >= 400) {
        toast.error("Activate Failed", data?.data?.detail || `Activation failed (${data?.status}).`);
        return;
      }

      toast.success("Activated!", `"${activateValidity.label}" is now active.`);
      setActivateValidity(null);
      fetchValidities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not activate. Please try again.");
    } finally {
      setActivatingId(null);
    }
  };

  const openEdit = async (validity) => {
    try {
      const { data } = await api.get(validity.id);
      const src = data?.data ?? data ?? validity;
      setEditValidity({
        id: src.id,
        label: src.label ?? "",
        value: src.value ?? "",
        unit: src.unit ?? "month",
        sort_order: src.sort_order ?? "",
        is_active: src.is_active ?? true,
        days_equivalent: src.days_equivalent,
      });
    } catch {
      setEditValidity({
        id: validity.id,
        label: validity.label ?? "",
        value: validity.value ?? "",
        unit: validity.unit ?? "month",
        sort_order: validity.sort_order ?? "",
        is_active: validity.is_active ?? true,
        days_equivalent: validity.days_equivalent,
      });
    }
  };

  const hasFilters = search || filterActive;
  const inputBase = { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, background: "#f8fafc", color: "#1e293b", outline: "none", fontFamily: "inherit" };
  const COLS = ["Sr No.", "Label", "Value", "Unit", "Days Equivalent", "Status", "Created At", "Actions"];

  const unitLabel = (unit) => {
    const map = { day: "Day", week: "Week", month: "Month" };
    return map[unit] || unit;
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: 24, background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>
      <style>{`
        @keyframes vmShimmer  { 0%   { background-position:  200% 0; } 100% { background-position: -200% 0; } }
        @keyframes vmFadeUp   { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes vmIn       { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes vmOut      { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        @keyframes spin       { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .vm-row:hover         { background: #f8fafc !important; }
        .vm-edit-btn:hover    { background: #dbeafe !important; }
        .vm-del-btn:hover     { background: #ffe4e6 !important; }
        .vm-act-btn:hover     { background: #bbf7d0 !important; }
        .vm-add-btn:hover     { background: #2563eb !important; }
        .vm-page-btn:hover    { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>Delivery Timeline Master</h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>Manage delivery validity / timelines</p>
        </div>
        <button className="vm-add-btn" onClick={() => setShowAdd(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.15s" }}>
          <PlusIcon /> Add Delivery Timeline
        </button>
      </div>

      {/* Stats */}
      {/* <StatsBar validities={allValidities} loading={loading} /> */}

      {/* Timeline visual */}
      {/* {!loading && allValidities.some((v) => v.is_active) && (
        <DeliveryTimeline validities={allValidities} />
      )} */}

      {/* Search & Filter bar */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #e2e8f0", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon /></span>
          <input
            style={{ ...inputBase, width: "100%", paddingLeft: 34, boxSizing: "border-box" }}
            placeholder="Search validities…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
            onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
          />
        </div>

        <select style={{ ...inputBase, cursor: "pointer", flex: "0 1 140px" }} value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        {hasFilters && (
          <button onClick={() => { setSearch(""); setFilterActive(""); }}
            style={{ ...inputBase, background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca", cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>
                {COLS.map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton />
              ) : allValidities.length === 0 ? (
                <tr><td colSpan={COLS.length}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#374151" }}>No  Timelines found</p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{hasFilters ? "Try adjusting your filters" : "Add your first validity period to get started"}</p>
                    {!hasFilters && (
                      <button onClick={() => setShowAdd(true)}
                        style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                        <PlusIcon /> Add Delivery Timeline
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : (
                pagedValidities.map((validity, idx) => {
                  const isBusy = deletingId === validity.id || activatingId === validity.id;
                  const colorIdx = allValidities.findIndex((v) => v.id === validity.id) % TIMELINE_COLORS.length;
                  const color = TIMELINE_COLORS[colorIdx];
                  return (
                    <tr key={validity.id} className="vm-row"
                      style={{ borderBottom: "1px solid #f1f5f9", opacity: isBusy ? 0.35 : 1, pointerEvents: isBusy ? "none" : "auto", transition: "opacity 0.2s" }}>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 13 }}>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <span style={{ fontWeight: 700, background: color.bg, color: color.text, padding: "3px 10px", borderRadius: 20, fontSize: 13 }}>
                          {validity.label}
                        </span>
                      </td>
                      <td style={{ padding: "13px 16px", fontWeight: 600, color: "#334155" }}>{validity.value}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <span style={{ fontSize: 12.5, background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>
                          {unitLabel(validity.unit)}
                        </span>
                      </td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 48, height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.min((validity.days_equivalent / 360) * 100, 100)}%`, background: color.bar, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{validity.days_equivalent} days</span>
                        </div>
                      </td>
                      {/* <td style={{ padding: "13px 16px", color: "#64748b", fontSize: 13 }}>{validity.sort_order ?? "—"}</td> */}
                      <td style={{ padding: "13px 16px" }}><Badge active={validity.is_active} /></td>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>{validity.created_at || "—"}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="vm-edit-btn" title="Edit" onClick={() => openEdit(validity)}
                            style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                            <EditIcon /> Edit
                          </button>
                          {validity.is_active ? (
                            <button className="vm-del-btn" title="Deactivate" onClick={() => setDeleteValidity(validity)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 6, cursor: "pointer", color: "#f43f5e", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                              Deactivate
                            </button>
                          ) : (
                            <button className="vm-act-btn" title="Activate" onClick={() => setActivateValidity(validity)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, cursor: "pointer", color: "#16a34a", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && allValidities.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount}
            onPageChange={(p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); }} />
        )}
      </div>

      {showAdd && (
        <Modal title="Add New Timeline" onClose={() => setShowAdd(false)}>
          <DeliveryTimelineForm
            onSubmit={handleAdd}
            onCancel={() => setShowAdd(false)}
            loading={actionLoading}
            existingValidities={allValidities}
            editingId={null}
          />
        </Modal>
      )}

      {editValidity && (
        <Modal title="Edit Timeline" onClose={() => setEditValidity(null)}>
          <DeliveryTimelineForm
            initial={editValidity}
            onSubmit={handleUpdate}
            onCancel={() => setEditValidity(null)}
            loading={actionLoading}
            existingValidities={allValidities}
            editingId={editValidity.id}
          />
        </Modal>
      )}

      {deleteValidity && (
        <Modal title="Deactivate Timeline" onClose={() => setDeleteValidity(null)}>
          <DeactivateConfirm
            validity={deleteValidity}
            onConfirm={handleDelete}
            onCancel={() => setDeleteValidity(null)}
            loading={actionLoading}
          />
        </Modal>
      )}

      {activateValidity && (
        <Modal title="Activate Timeline" onClose={() => setActivateValidity(null)}>
          <ActivateConfirm
            validity={activateValidity}
            onConfirm={handleActivateConfirm}
            onCancel={() => setActivateValidity(null)}
            loading={!!activatingId}
          />
        </Modal>
      )}
    </div>
  );
};

export default DeliveryTimelineMaster;